import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { Button } from "@workspace/ui/components/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@workspace/ui/components/card";
import { Textarea } from "@workspace/ui/components/textarea";
import { RotateCcwIcon, SaveIcon, SparklesIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { orpc } from "@/api/orpc";
import { Main } from "@/layout/main";
import { getError } from "@/utils/error";

export const Route = createFileRoute("/_authenticated/ai-prompts")({
  beforeLoad: ({ context }) => {
    if (context.dashboardRole !== "admin") throw redirect({ to: "/403", replace: true });
  },
  component: AiPromptsPage,
});

function AiPromptsPage() {
  const queryClient = useQueryClient();
  const promptQuery = useQuery(orpc.admin.aiPrompts.projectOverview.get.queryOptions());
  const [template, setTemplate] = useState("");
  const requiredPlaceholders = ["{{language}}", "{{params}}", "{{previousOverview}}"];
  const hasRequiredPlaceholders = requiredPlaceholders.every((placeholder) => template.includes(placeholder));

  useEffect(() => {
    if (promptQuery.data) setTemplate(promptQuery.data.template);
  }, [promptQuery.data]);

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: orpc.admin.aiPrompts.projectOverview.get.queryKey() });
  };

  const updatePrompt = useMutation(
    orpc.admin.aiPrompts.projectOverview.update.mutationOptions({
      onSuccess: async () => {
        await invalidate();
        toast.success("Project content prompt saved");
      },
      onError: (error) => toast.error(getError(error, "Unable to save the prompt")),
    }),
  );

  const resetPrompt = useMutation(
    orpc.admin.aiPrompts.projectOverview.reset.mutationOptions({
      onSuccess: async (data) => {
        setTemplate(data.template);
        await invalidate();
        toast.success("Default prompt restored");
      },
      onError: (error) => toast.error(getError(error, "Unable to restore the default prompt")),
    }),
  );

  if (promptQuery.isLoading) return <Main>Loading prompt settings…</Main>;
  if (promptQuery.isError) return <Main>Unable to load prompt settings.</Main>;

  const isBusy = updatePrompt.isPending || resetPrompt.isPending;

  return (
    <Main className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-semibold text-2xl">AI content prompts</h1>
          <p className="mt-1 text-muted-foreground text-sm">
            Administrator-only settings for project content generation.
          </p>
        </div>
        <span className="rounded-full border px-3 py-1 font-medium text-sm">
          {promptQuery.data?.isCustom ? "Custom prompt" : "Default prompt"}
        </span>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <SparklesIcon className="size-5" />
            Project overview generator
          </CardTitle>
          <CardDescription>
            This prompt is used when an administrator generates Arabic project content from the project editor.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="rounded-md border bg-muted/40 p-3 text-muted-foreground text-sm">
            Keep these placeholders unchanged so the generator receives project data: <code>{"{{language}}"}</code>,{" "}
            <code>{"{{params}}"}</code>, and <code>{"{{previousOverview}}"}</code>.
          </div>
          <Textarea
            aria-label="Project overview AI prompt"
            className="min-h-[36rem] resize-y font-mono text-sm leading-6"
            value={template}
            onChange={(event) => setTemplate(event.target.value)}
          />
          <div className="flex flex-wrap justify-end gap-3">
            <Button type="button" variant="outline" disabled={isBusy} onClick={() => resetPrompt.mutate(undefined)}>
              <RotateCcwIcon />
              Restore default
            </Button>
            <Button
              type="button"
              disabled={isBusy || template.trim().length < 100 || !hasRequiredPlaceholders}
              onClick={() => updatePrompt.mutate({ template: template.trim() })}
            >
              <SaveIcon />
              Save prompt
            </Button>
          </div>
        </CardContent>
      </Card>
    </Main>
  );
}
